import {
  CART_STATE_VERSION,
  cartStateSchema,
  type CartLine,
  type CartState,
} from "./cart.model";

/**
 * El carrito, guardado en el navegador.
 *
 * ── Por qué se persiste ──────────────────────────────────────────────
 * Recargar la página no debería vaciar el carrito. Pasa todo el rato:
 * alguien abre la ficha de un producto en otra pestaña, el móvil
 * descarta la pestaña por memoria, o la red se corta y se pulsa F5. Un
 * carrito que se evapora en cualquiera de esos casos se abandona.
 *
 * ── Por qué se VALIDA al leer ────────────────────────────────────────
 * Lo que hay en `localStorage` es entrada de fuera: lo escribió una
 * versión anterior del código, lo tocó una extensión, o lo editó alguien
 * a mano desde la consola. Sin validar, un `quantity: "3"` en texto
 * acabaría concatenando en vez de sumando, y un `quantity: -5` restaría
 * del subtotal. El esquema de Zod convierte todo eso en "no había
 * carrito", que es un estado que la interfaz ya sabe pintar.
 *
 * El número de versión va DENTRO del documento y también en la clave: la
 * clave separa formatos incompatibles de un vistazo, y el campo `v` deja
 * que una futura migración distinga qué está leyendo.
 */
export const CART_STORAGE_KEY = "nh_cart_v1";

/**
 * Lee el carrito guardado. Devuelve `[]` ante CUALQUIER problema.
 *
 * `localStorage` no solo devuelve `null` cuando no hay nada: LANZA en
 * navegación privada de algunos navegadores y con las cookies de terceros
 * bloqueadas dentro de un iframe. Sin este try, la tienda entera se cae
 * en blanco por un carrito.
 */
export function readCart(): CartLine[] {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(CART_STORAGE_KEY);
  } catch {
    return [];
  }

  if (!raw) return [];

  let payload: unknown;
  try {
    payload = JSON.parse(raw);
  } catch {
    return [];
  }

  const parsed = cartStateSchema.safeParse(payload);
  if (!parsed.success) return [];

  return parsed.data.lines;
}

/** Guarda el carrito. No poder guardarlo no impide usarlo en esta visita. */
export function writeCart(lines: readonly CartLine[]): void {
  const state: CartState = { v: CART_STATE_VERSION, lines: [...lines] };
  try {
    window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Cuota llena o almacenamiento bloqueado. El carrito sigue vivo en
    // memoria; lo único que se pierde es sobrevivir a la recarga.
  }
}
